import { useState } from 'react'
import { Menu } from '@mui/material'
import type { MenuSchedule } from '@/shared/api/location'
import { formatTimeStr, getCurrentScheduleHour } from '@/lib/timeUtils'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import MenuBookTwoToneIcon from '@mui/icons-material/MenuBookTwoTone'
import {
	PillRoot,
	PillLabel,
	PillLabelBold,
	PillLabelNormal,
	PillIconWrap,
	DropdownItem,
	dropdownPaperSx,
} from './MenuSchedulePill.styled'

interface MenuSchedulePillProps {
	schedule: MenuSchedule
	schedules: MenuSchedule[]
	onSelect: (schedule: MenuSchedule) => void
	fullWidth?: boolean
	compact?: boolean
}

export function MenuSchedulePill({
	schedule,
	schedules,
	onSelect,
	fullWidth,
	compact,
}: MenuSchedulePillProps) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const open = Boolean(anchorEl)
	const isInteractive = schedules.length > 1

	const currentHour = getCurrentScheduleHour(schedule)
	const timeRange = currentHour
		? `${formatTimeStr(currentHour.startTime)} – ${formatTimeStr(currentHour.endTime)}`
		: null

	function handleOpen(e: React.MouseEvent<HTMLElement>) {
		if (isInteractive) setAnchorEl(e.currentTarget)
	}

	function handleClose() {
		setAnchorEl(null)
	}

	function handleSelect(s: MenuSchedule) {
		onSelect(s)
		setAnchorEl(null)
	}

	return (
		<>
			<PillRoot
				onClick={handleOpen}
				data-testid="menu-schedule-pill"
				isInteractive={isInteractive}
				fullWidth={fullWidth}
				compact={compact}
				aria-haspopup={isInteractive ? 'listbox' : undefined}
				aria-expanded={open || undefined}
				aria-disabled={!isInteractive || undefined}
			>
				<MenuBookTwoToneIcon sx={{ fontSize: 20 }} aria-hidden="true" />
				<PillLabel>
					<PillLabelBold>{schedule.name}</PillLabelBold>
					{timeRange && <PillLabelNormal>{timeRange}</PillLabelNormal>}
				</PillLabel>
				{isInteractive && (
					<PillIconWrap>
						<ExpandMoreOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
					</PillIconWrap>
				)}
			</PillRoot>

			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{ paper: { sx: dropdownPaperSx } }}
				disableScrollLock
			>
				{schedules.map((s) => {
					const itemHour = getCurrentScheduleHour(s)
					const itemTimeRange = itemHour
						? `${formatTimeStr(itemHour.startTime)} – ${formatTimeStr(itemHour.endTime)}`
						: null
					return (
						<DropdownItem
							key={s.ref}
							selected={s.ref === schedule.ref}
							onClick={() => handleSelect(s)}
						>
							<PillLabelBold>{s.name}</PillLabelBold>
							{itemTimeRange && <PillLabelNormal> ({itemTimeRange})</PillLabelNormal>}
						</DropdownItem>
					)
				})}
			</Menu>
		</>
	)
}
